import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Instagram, Star } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_shamrock-preview/artifacts/4r45em6p_WhatsApp%20Image%202026-04-27%20at%201.33.14%20AM.jpeg";

const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#050a09] border-t border-white/5 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <img 
                src={LOGO_URL} 
                alt="The Shamrock" 
                className="h-16 w-auto object-contain"
              />
            </Link>
            <p className="text-[#A3A3A3] text-sm leading-relaxed font-['Manrope'] mb-4">
              A Modern Chapter in Irish Tradition. Finest spirits, signature cocktails, and authentic Irish hospitality in the heart of Bern.
            </p>
            {/* Social Links */}
            <div className="flex items-center space-x-4">
              <a 
                href="https://instagram.com/theshamrockbern" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center hover:bg-[#D4AF37]/20 transition-colors"
              >
                <Instagram size={18} className="text-[#D4AF37]" />
              </a>
              <a 
                href="https://g.page/r/CSMBOol6DR0FEAE/review" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-reviews"
                className="w-10 h-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center hover:bg-[#D4AF37]/20 transition-colors"
              >
                <Star size={18} className="text-[#D4AF37]" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg font-semibold text-[#D4AF37] mb-4">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { to: '/', label: 'Home' },
                { to: '/events', label: 'Events' },
                { to: '/menu', label: 'Menu' },
                { to: '/reservations', label: 'Reservations' },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to}
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                    className="text-[#E8E6D9] hover:text-[#D4AF37] transition-colors text-sm font-['Manrope']"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <a 
                  href="https://g.page/r/CSMBOol6DR0FEAE/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-link-reviews"
                  className="text-[#E8E6D9] hover:text-[#D4AF37] transition-colors text-sm font-['Manrope']"
                >
                  Leave a Review
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg font-semibold text-[#D4AF37] mb-4">
              Contact
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <a 
                  href="https://maps.google.com/?q=Schwarztorstrasse+11,+3007+Bern"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E8E6D9] hover:text-[#D4AF37] transition-colors text-sm font-['Manrope']"
                >
                  Schwarztorstrasse 11<br />3007 Bern, Switzerland
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Phone size={18} className="text-[#D4AF37] flex-shrink-0" />
                <a 
                  href="tel:+41337227000" 
                  data-testid="footer-phone"
                  className="text-[#E8E6D9] hover:text-[#D4AF37] transition-colors text-sm font-['Manrope']"
                >
                  033 722 70 00
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail size={18} className="text-[#D4AF37] flex-shrink-0" />
                <a 
                  href="mailto:bern@theshamrock.ch"
                  data-testid="footer-email"
                  className="text-[#E8E6D9] hover:text-[#D4AF37] transition-colors text-sm font-['Manrope']"
                >
                  bern@theshamrock.ch
                </a>
              </li>
            </ul>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg font-semibold text-[#D4AF37] mb-4">
              Opening Hours
            </h4>
            <ul className="space-y-1.5 text-sm font-['Manrope']">
              <li className="flex justify-between text-[#E8E6D9]">
                <span>Mon - Thu</span>
                <span>11:00 - 23:00</span>
              </li>
              <li className="flex justify-between text-[#E8E6D9]">
                <span>Friday</span>
                <span>11:00 - 01:00</span>
              </li>
              <li className="flex justify-between text-[#E8E6D9]">
                <span>Saturday</span>
                <span>16:00 - 01:00</span>
              </li>
              <li className="flex justify-between text-[#E8E6D9]">
                <span>Sunday</span>
                <span>16:00 - 23:00</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/5 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-[#525252] text-sm font-['Manrope']">
              © {new Date().getFullYear()} The Shamrock. All rights reserved.
            </p>
            <p className="text-[#525252] text-sm font-['Manrope']">
              Crafted with ☘ in Bern, Switzerland
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
